import { useEffect, useState } from 'react';
import { useAnimate } from 'motion/react';
import BorderImage from '@/assets/images/1-circles-dashes.svg';
import Logo from '@/assets/images/1-find-the-password.png';
import { useBackgroundAnimation } from '@/hooks';
import { CodeInput } from './CodeInput';
import { KEY_ANIMATION_DELAY_BETWEEN_MILLISECONDS, KEY_ANIMATION_FADING_MILLISECONDS, KEYS_CSS_SELECTOR_PREFIX, KEYS_FOR_ANIMATION, SHAPES } from './config';

interface Props {
  onComplete: () => void;
  onExitStart: () => void;
}

export const Step1 = ({ onComplete, onExitStart }: Props) => {
  const [lockedCodes, setLockedCodes] = useState<boolean[]>([false, false, false]);
  const backgroundAnimationScope = useBackgroundAnimation(
    KEYS_FOR_ANIMATION,
    KEYS_CSS_SELECTOR_PREFIX,
    KEY_ANIMATION_FADING_MILLISECONDS,
    KEY_ANIMATION_DELAY_BETWEEN_MILLISECONDS,
  );
  const [pageTransitionScope, animate] = useAnimate();

  useEffect(() => {
    const enterAnimation = async () => {
      // initial state, instant
      await animate(
        pageTransitionScope.current,
        {
          opacity: 0,
          background: 'var(--color-beige)',
        },
        { duration: 0 },
      );

      // actual animation
      await animate(pageTransitionScope.current, { opacity: 1 }, { duration: 1, ease: 'easeInOut' });
    };

    void enterAnimation();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exitAnimation = async () => {
    void animate('&>*', { opacity: 0 }, { duration: 0.5 });

    return animate(pageTransitionScope.current, { background: 'var(--color-green)' }, { duration: 2 });
  };

  const handleCodeCorrect = (codeIndex: number) => {
    const newLockedCodes = [...lockedCodes];
    newLockedCodes[codeIndex] = true;
    setLockedCodes(newLockedCodes);

    if (newLockedCodes.every(locked => locked)) {
      handleStepSuccess();
    }
  };

  const handleStepSuccess = () => {
    onExitStart();
    void exitAnimation().then(onComplete);
  };

  return (
    <div className="relative min-h-dvh w-screen bg-beige flex flex-col overflow-hidden">
      <div className="flex w-full">
        <div className="flex-1 p-3 bg-beige flex items-center justify-center text-center text-dark-beige font-bold">Find the&nbsp;Password</div>
        <div className="flex-1 p-3 bg-green flex items-center justify-center text-center text-light-green/50">*********</div>
        <div className="flex-1 p-3 bg-red flex items-center justify-center text-center text-dark-red/50">*********</div>
      </div>

      <div
        ref={pageTransitionScope}
        className="relative flex-1 flex flex-col items-center justify-center gap-8 md:gap-32 p-4 border-image-dashed text-dark-beige"
        style={{
          borderImageSource: `url(${BorderImage})`,
        }}
      >
        <div ref={backgroundAnimationScope} className="absolute size-full">
          {KEYS_FOR_ANIMATION.map((SVGElement, index) => (
            <SVGElement
              key={index}
              className={`${KEYS_CSS_SELECTOR_PREFIX}-${index} absolute pointer-events-none *:max-w-full *:max-h-full text-current/25 size-28 md:size-44`}
              style={{ opacity: 0 }}
            />
          ))}
        </div>

        <img src={Logo} alt="Find the P***word" className="h-48 md:h-64 z-10" />

        <div className="flex flex-col md:flex-row gap-12 z-10">
          {SHAPES.map((_, index) => (
            <CodeInput key={index} codeIndex={index} locked={lockedCodes[index]} onCorrect={() => handleCodeCorrect(index)} />
          ))}
        </div>
      </div>
    </div>
  );
};
